import React from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, ArrowRight } from 'lucide-react';
import newsSigning from '../../assets/news-signing.png';
import newsPresentation from '../../assets/news-presentation.png';
import newsPartnership from '../../assets/news-partnership.png';

const NewsSection = () => {
  const { i18n } = useTranslation();

  const newsItems = [
    {
      id: 1,
      titleAr: 'إنجازات قانونية متواصلة',
      titleEn: 'Ongoing Legal Achievements',
      descriptionAr:
        'نواصل في وازن معالجة عدد من القضايا في مجالات متعددة، مع الحرص على تقديم حلول قانونية دقيقة تحفظ مصالح عملائنا وتدعم مراكزهم النظامية.',
      descriptionEn:
        "At Wazin, we continue handling a range of cases across multiple fields, with a commitment to delivering precise legal solutions that protect our clients' interests and strengthen their legal positions.",
      date: '2025-01-15',
      category: 'إنجاز',
      categoryEn: 'Achievement',
      image: newsSigning
    },
    {
      id: 2,
      titleAr: 'عقود مستمرة وخدمات متجددة',
      titleEn: 'Ongoing Contracts and Renewed Services',
      descriptionAr:
        'تواصل وازن تقديم خدمات صياغة العقود ومراجعتها ومتابعتها باحترافية، بما يحقق وضوح الالتزامات ويعزز حماية الحقوق للأفراد وقطاع الأعمال.',
      descriptionEn:
        'Wazin continues to provide professional contract drafting, review, and follow-up services that ensure clarity of obligations and strengthen the protection of rights for individuals and businesses.',
      date: '2025-02-10',
      category: 'خدمة',
      categoryEn: 'Service',
      image: newsPresentation
    },
    {
      id: 3,
      titleAr: 'رؤية عملية في تقديم الخدمة القانونية',
      titleEn: 'A Practical Vision for Delivering Legal Services',
      descriptionAr:
        'نعمل في وازن على تطوير تجربة تقديم الخدمة القانونية بصورة أكثر كفاءة وتنظيمًا، مع التركيز على الجودة، وسرعة الاستجابة، وبناء الثقة مع عملائنا.',
      descriptionEn:
        'At Wazin, we are advancing the legal service experience to be more efficient and organized, with a strong focus on quality, responsiveness, and building trust with our clients.',
      date: '2025-01-28',
      category: 'مستجد',
      categoryEn: 'Update',
      image: newsPartnership
    }
  ];

  return (
    <section className="py-24 px-4 bg-gray-50" id="news">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-20">
          <h2 className="text-4xl md:text-5xl font-bold text-wazin-navy mb-4">
            {i18n.language === 'ar' ? 'مستجدات وازن' : 'Wazin Updates'}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {i18n.language === 'ar'
              ? 'تابع أبرز المستجدات، والإنجازات، والمحتوى القانوني من وازن.'
              : "Follow Wazin's latest updates, achievements, and legal content."}
          </p>
        </div>

        {/* News Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {newsItems.map((item) => (
            <div
              key={item.id}
              className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer"
              dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
            >
              {/* Image - Uniform Aspect Ratio */}
              <div className="relative h-64 overflow-hidden">
                <img
                  src={item.image}
                  alt={i18n.language === 'ar' ? item.titleAr : item.titleEn}
                  className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-500"
                />
                {/* Category Badge */}
                <div className="absolute top-4 right-4 bg-wazin-gold text-wazin-navy px-4 py-2 rounded-full font-bold text-sm shadow-lg">
                  {i18n.language === 'ar' ? item.category : item.categoryEn}
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Date */}
                <div className="flex items-center gap-2 text-gray-500 text-sm mb-3">
                  <Calendar className="w-4 h-4" />
                  <span>
                    {new Date(item.date).toLocaleDateString(i18n.language === 'ar' ? 'ar-SA' : 'en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </span>
                </div>

                {/* Title */}
                <h3 className="text-xl font-bold text-wazin-navy mb-3 line-clamp-2 group-hover:text-wazin-gold transition-colors duration-250">
                  {i18n.language === 'ar' ? item.titleAr : item.titleEn}
                </h3>

                {/* Description */}
                <p className="text-gray-600 mb-4 line-clamp-3">
                  {i18n.language === 'ar' ? item.descriptionAr : item.descriptionEn}
                </p>

                {/* Read More Link */}
                <button className="flex items-center gap-2 text-wazin-gold hover:text-wazin-gold-dark font-semibold transition-colors duration-250 group">
                  <span>{i18n.language === 'ar' ? 'اقرأ المزيد' : 'Read More'}</span>
                  <ArrowRight className={`w-4 h-4 group-hover:translate-x-1 transition-transform ${i18n.language === 'ar' ? 'rotate-180' : ''}`} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* View All Button */}
        <div className="text-center mt-12">
          <button className="px-8 py-4 bg-wazin-gold text-white font-bold rounded-lg hover:bg-wazin-gold-dark transition-all duration-250 shadow-lg hover:shadow-xl">
            {i18n.language === 'ar' ? 'عرض جميع المستجدات' : 'View All Updates'}
          </button>
        </div>
      </div>
    </section>
  );
};

export default NewsSection;
